import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    ArrowLeft, 
    Building2, 
    Calendar, 
    Mail, 
    Phone, 
    Globe, 
    MapPin,
    CreditCard,
    Users,
    GraduationCap,
    School,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Clock,
    MoreHorizontal,
    Plus,
    History,
    ShieldCheck,
    Loader2,
    Save
} from 'lucide-react';

const StatusBadge = ({ status }) => {
    const configs = {
        active: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
        trial: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
        suspended: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle },
        expired: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
        cancelled: { bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle },
    };
    const config = configs[status] || configs.cancelled;
    const Icon = config.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${config.bg} ${config.text}`}>
            <Icon className="h-3 w-3" />
            {status}
        </span>
    );
};

const InstitutionDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [institution, setInstitution] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [statusModal, setStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [packages, setPackages] = useState([]);
    const [selectedPackageId, setSelectedPackageId] = useState('');
    const [billingApproach, setBillingApproach] = useState('');
    const [priceOverride, setPriceOverride] = useState('');
    const [commissionOverride, setCommissionOverride] = useState('');
    const [billingMpesaNumber, setBillingMpesaNumber] = useState('');

    useEffect(() => {
        fetchInstitutionDetail();
    }, [id]);

    const fetchInstitutionDetail = async () => {
        try {
            const [instResponse, pkgsResponse] = await Promise.all([
                axios.get(`/super-admin/institutions/${id}`),
                axios.get(`/super-admin/packages`)
            ]);
            setInstitution(instResponse.data);
            setPackages(pkgsResponse.data || []);

            const sub = instResponse.data.subscription;
            if (sub) {
                setNewStatus(sub.status || '');
                setSelectedPackageId(sub.packageId || '');
                setBillingApproach(sub.billingApproach || '');
                setPriceOverride(sub.pricePerStudentPerUnit !== null && sub.pricePerStudentPerUnit !== undefined ? String(sub.pricePerStudentPerUnit) : '');
                setCommissionOverride(sub.commissionPercentage !== null && sub.commissionPercentage !== undefined ? String(sub.commissionPercentage) : '');
                setBillingMpesaNumber(sub.billingMpesaNumber || '');
            }
        } catch (error) {
            console.error('Error fetching institution detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async () => {
        setUpdating(true);
        try {
            await axios.put(`/super-admin/institutions/${id}/subscription`, {
                status: newStatus,
                packageId: selectedPackageId ? parseInt(selectedPackageId) : undefined,
                billingApproach: billingApproach || null,
                pricePerStudentPerUnit: priceOverride === '' ? null : parseFloat(priceOverride),
                commissionPercentage: commissionOverride === '' ? null : parseFloat(commissionOverride),
                billingMpesaNumber: billingMpesaNumber || null
            });
            await fetchInstitutionDetail();
            setStatusModal(false);
        } catch (error) {
            alert('Failed to update status: ' + (error.response?.data?.message || error.message));
        } finally {
            setUpdating(false);
        }
    };

    const handleToggleSuspend = async () => {
        const sub = institution?.subscription;
        if (!sub) return;
        const isSuspended = sub.status === 'suspended';
        const targetStatus = isSuspended ? 'active' : 'suspended';
        const confirmMsg = isSuspended
            ? "Are you sure you want to reactivate this institution's account?"
            : "Are you sure you want to suspend this institution's account? All admins, lecturers, and students will be immediately blocked from accessing the system.";

        if (!window.confirm(confirmMsg)) return;

        setUpdating(true);
        try {
            await axios.put(`/super-admin/institutions/${id}/subscription`, {
                status: targetStatus,
                packageId: sub.packageId,
                billingApproach: sub.billingApproach,
                pricePerStudentPerUnit: sub.pricePerStudentPerUnit,
                commissionPercentage: sub.commissionPercentage,
                billingMpesaNumber: sub.billingMpesaNumber
            });
            await fetchInstitutionDetail();
            alert(`Account successfully ${isSuspended ? 'reactivated' : 'suspended'}.`);
        } catch (error) {
            alert('Failed to toggle suspension: ' + (error.response?.data?.message || error.message));
        } finally {
            setUpdating(false);
        }
    };

    const handleExtendTrial = async () => {
        if (!window.confirm('Extend trial by 14 days?')) return;
        setUpdating(true);
        try {
            await axios.post(`/super-admin/institutions/${id}/extend-trial`, { days: 14 });
            await fetchInstitutionDetail();
        } catch (error) {
            alert('Failed to extend trial');
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-[60vh]">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
        </div>
    );

    if (!institution) return <div className="text-center py-12 text-slate-500 font-bold">Institution not found</div>;

    const sub = institution.subscription;

    return (
        <div className="max-w-6xl mx-auto space-y-8 p-4">
            <button 
                onClick={() => navigate('/super-admin/institutions')}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-900 font-bold text-sm transition-colors group"
            >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> Back to Institutions
            </button>

            {/* Header Section */}
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-blue-600"></div>
                <div className="flex flex-col md:flex-row justify-between gap-8">
                    <div className="flex items-start gap-6">
                        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center text-3xl font-black text-slate-400 border border-slate-50 shadow-inner">
                            {institution.name[0]}
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h1 className="text-3xl font-black text-slate-900 tracking-tight">{institution.name}</h1>
                                <StatusBadge status={sub?.status} />
                            </div>
                            <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-sm font-medium text-slate-500">
                                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-300" /> {institution.city}, {institution.country}</span>
                                <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-slate-300" /> Since {new Date(institution.createdAt).toLocaleDateString()}</span>
                                <span className="px-2 py-0.5 bg-slate-100 rounded-md text-[10px] font-black uppercase text-slate-600">ID: #{institution.id}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button 
                            onClick={() => setStatusModal(true)}
                            className="px-6 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
                        >
                            Manage Subscription
                        </button>
                        <button className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 transition-all">
                            <MoreHorizontal className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 pt-8 border-t border-slate-50">
                    <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Learning Mode</p>
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                            <School className="h-4 w-4 text-blue-600" />
                            {institution.learningMode === 'instructor_led' ? 'Instructor-Led' : 'Self-Paced'}
                        </p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Students</p>
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                            <Users className="h-4 w-4 text-blue-600" />
                            {institution.stats?.totalStudents}
                        </p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Lecturers</p>
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                            <GraduationCap className="h-4 w-4 text-indigo-600" />
                            {institution.stats?.totalLecturers}
                        </p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Courses</p>
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                            <ShieldCheck className="h-4 w-4 text-green-600" />
                            {institution.stats?.totalCourses}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Details */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Subscription & Billing */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
                            <h3 className="font-black text-slate-900 flex items-center gap-2">
                                <CreditCard className="h-5 w-5 text-blue-600" /> Subscription & Billing History
                            </h3>
                            <button className="text-xs font-bold text-blue-600 hover:underline">Download Report</button>
                        </div>
                        <div className="p-0">
                            {sub ? (
                                <>
                                    <div className="p-6 bg-blue-50/30 grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-blue-50">
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Current Package</p>
                                            <p className="text-base font-bold text-slate-900">{sub.subscription_package?.displayName || 'Basic Plan'}</p>
                                            
                                            {/* Dynamic Billing Rate Display */}
                                            {(() => {
                                                const approach = sub.billingApproach || sub.subscription_package?.billingApproach || 'unit_fixed';
                                                if (approach === 'unit_commission') {
                                                    const isOverridden = sub.commissionPercentage !== null && sub.commissionPercentage !== undefined;
                                                    const rate = isOverridden ? sub.commissionPercentage : (sub.subscription_package?.commissionPercentage || 0);
                                                    return (
                                                        <div className="mt-1">
                                                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[9px] font-black uppercase rounded-md mr-1.5">Commission</span>
                                                            <p className="text-xs text-indigo-600 font-bold inline-block">
                                                                {rate}% of Tuition per Unit {isOverridden && <span className="text-[10px] text-amber-600 font-medium font-mono">(Custom)</span>}
                                                            </p>
                                                        </div>
                                                    );
                                                } else {
                                                    const isOverridden = sub.pricePerStudentPerUnit !== null && sub.pricePerStudentPerUnit !== undefined;
                                                    const price = isOverridden ? sub.pricePerStudentPerUnit : (sub.subscription_package?.pricePerStudentPerUnit || 0);
                                                    return (
                                                        <div className="mt-1">
                                                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-black uppercase rounded-md mr-1.5">Fixed Rate</span>
                                                            <p className="text-xs text-emerald-600 font-bold inline-block">
                                                                KES {parseFloat(price).toLocaleString()} / std / unit {isOverridden && <span className="text-[10px] text-amber-600 font-medium font-mono">(Custom)</span>}
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                            })()}
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Trial Status</p>
                                            {sub.isInTrial ? (
                                                <div className="flex flex-col">
                                                    <p className="text-base font-bold text-blue-600">In Trial</p>
                                                    <p className="text-[11px] font-medium text-slate-500">Expires {new Date(sub.trialEndDate).toLocaleDateString()} ({Math.ceil((new Date(sub.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24))} days left)</p>
                                                    <button 
                                                        onClick={handleExtendTrial}
                                                        className="mt-2 text-left text-[10px] font-black text-blue-600 uppercase hover:underline"
                                                    >
                                                        + Extend by 14 days
                                                    </button>
                                                </div>
                                            ) : (
                                                <p className="text-base font-bold text-green-600">Full Subscription</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Auto-Billing Mobile</p>
                                            {sub.billingMpesaNumber ? (
                                                <div className="flex flex-col mt-1">
                                                    <p className="text-base font-bold text-slate-900 font-mono">{sub.billingMpesaNumber}</p>
                                                    <p className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 mt-0.5">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                                                        Automated Payout active
                                                    </p>
                                                </div>
                                            ) : (
                                                <p className="text-xs text-slate-450 mt-1.5 italic font-medium">No Mpesa billing number set</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead>
                                                <tr className="border-b border-slate-50">
                                                    <th className="px-6 py-3 font-bold text-slate-400 text-xs">Period</th>
                                                    <th className="px-6 py-3 font-bold text-slate-400 text-xs text-center">Usage</th>
                                                    <th className="px-6 py-3 font-bold text-slate-400 text-xs text-right">Amount Due</th>
                                                    <th className="px-6 py-3 font-bold text-slate-400 text-xs text-right">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50">
                                                {sub.billing_histories?.map(bill => (
                                                    <tr key={bill.id} className="hover:bg-slate-50/50 transition-colors">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-2">
                                                                <History className="h-3 w-3 text-slate-300" />
                                                                <span className="font-semibold text-slate-700">
                                                                    {new Date(bill.billingPeriodStart).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="text-[11px] font-bold text-slate-500">
                                                                {bill.totalStudents} Std / {bill.totalUnits} Units
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right font-black text-slate-900">
                                                            KES {parseFloat(bill.amountDue).toLocaleString()}
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-md ${
                                                                bill.status === 'paid' ? 'bg-green-100 text-green-700' : 
                                                                bill.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                                            }`}>
                                                                {bill.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(!sub.billing_histories || sub.billing_histories.length === 0) && (
                                                    <tr>
                                                        <td colSpan="4" className="px-6 py-12 text-center text-slate-400 font-medium">No billing history yet</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div className="p-12 text-center text-slate-400 font-bold italic">No active subscription found</div>
                            )}
                        </div>
                    </div>

                    {/* Campuses */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-slate-50 flex items-center justify-between">
                            <h3 className="font-black text-slate-900">Campuses & Admins</h3>
                            <button className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all">
                                <Plus className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="divide-y divide-slate-50">
                            {institution.campuses?.map(campus => (
                                <div key={campus.id} className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h4 className="text-lg font-black text-slate-900">{campus.name}</h4>
                                            <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1 font-medium italic"><MapPin className="h-3 w-3" /> {campus.location}</p>
                                        </div>
                                        <button className="text-xs font-bold text-blue-600 px-3 py-1 bg-blue-50 rounded-lg">Manage</button>
                                    </div>
                                    <div className="space-y-3">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Assigned Admins</p>
                                        <div className="flex flex-wrap gap-4">
                                            {campus.admins?.map(admin => (
                                                <div key={admin.id} className="flex items-center gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-100 min-w-[240px]">
                                                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-slate-200 text-blue-600 font-black shadow-sm">
                                                        {admin.name[0]}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-slate-900">{admin.name}</p>
                                                        <p className="text-[11px] text-slate-500 font-medium">{admin.email}</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {(!campus.admins || campus.admins.length === 0) && (
                                                <p className="text-xs text-slate-400 italic font-medium">No admins assigned</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column: Contact Info */}
                <div className="space-y-8">
                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                        <h3 className="font-black text-slate-900 border-b border-slate-50 pb-4">Contact Information</h3>
                        <div className="space-y-4">
                            <div className="flex items-start gap-4">
                                <div className="p-2.5 bg-slate-100 rounded-xl">
                                    <Mail className="h-5 w-5 text-slate-500" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Email Address</p>
                                    <p className="text-sm font-bold text-slate-900 truncate">{institution.email || 'No email set'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="p-2.5 bg-slate-100 rounded-xl">
                                    <Phone className="h-5 w-5 text-slate-500" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Phone Number</p>
                                    <p className="text-sm font-bold text-slate-900">{institution.phone || 'No phone set'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="p-2.5 bg-slate-100 rounded-xl">
                                    <Globe className="h-5 w-5 text-slate-500" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Website</p>
                                    <p className="text-sm font-bold text-blue-600 truncate">{institution.website || 'No website set'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="p-2.5 bg-slate-100 rounded-xl">
                                    <MapPin className="h-5 w-5 text-slate-500" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Physical Address</p>
                                    <p className="text-sm font-bold text-slate-900 leading-relaxed">{institution.address}, {institution.city}, {institution.country}</p>
                                </div>
                            </div>
                        </div>
                        <button className="w-full py-3 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-black uppercase rounded-2xl hover:bg-slate-100 transition-all tracking-wider">
                            Edit Institution Info
                        </button>
                    </div>

                    <div className="bg-slate-900 p-8 rounded-3xl text-white relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:rotate-12 transition-transform duration-500">
                            <ShieldCheck className="h-24 w-24" />
                        </div>
                        <h4 className="text-xl font-black mb-2">Super Admin Actions</h4>
                        <p className="text-slate-400 text-xs leading-relaxed font-medium mb-6">These actions will impact the institution's access platform-wide. Use with caution.</p>
                        <div className="space-y-3">
                            <button 
                                onClick={handleToggleSuspend}
                                disabled={updating}
                                className={`
                                    w-full py-3 text-xs font-bold uppercase rounded-xl transition-all border
                                    ${sub?.status === 'suspended'
                                        ? 'bg-emerald-600/20 border-emerald-500/30 text-emerald-400 hover:bg-emerald-600 hover:text-white'
                                        : 'bg-red-600/20 border-red-500/30 text-red-400 hover:bg-red-600 hover:text-white'}
                                `}
                            >
                                {sub?.status === 'suspended' ? 'Reactivate Account' : 'Suspend Account'}
                            </button>
                            <button className="w-full py-3 bg-slate-800 text-slate-400 text-xs font-bold uppercase rounded-xl hover:bg-slate-700 hover:text-white transition-all">
                                Force Log Out All Users
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Manage Status Modal */}
            {statusModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setStatusModal(false)}></div>
                    <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-slate-100">
                        <div className="p-8 space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-black text-slate-900">Manage Subscription</h3>
                                <button onClick={() => setStatusModal(false)} className="p-2 text-slate-400 hover:text-slate-900">
                                    <ArrowLeft className="h-5 w-5" />
                                </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-h-[60vh] overflow-y-auto pr-2">
                                {/* Left Side: Status & Package */}
                                <div className="space-y-4">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider">Subscription Tier</h4>
                                    
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black uppercase text-slate-500">Active Package</label>
                                        <select 
                                            value={selectedPackageId}
                                            onChange={(e) => setSelectedPackageId(e.target.value)}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Select a package...</option>
                                            {packages.map(pkg => (
                                                <option key={pkg.id} value={pkg.id}>{pkg.displayName}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black uppercase text-slate-500">Status</label>
                                        <div className="grid grid-cols-1 gap-1.5">
                                            {['active', 'trial', 'suspended', 'expired', 'cancelled'].map(status => (
                                                <button 
                                                    key={status}
                                                    type="button"
                                                    onClick={() => setNewStatus(status)}
                                                    className={`
                                                        flex items-center justify-between px-4 py-2.5 rounded-xl border-2 transition-all text-left
                                                        ${newStatus === status 
                                                            ? 'bg-blue-50 border-blue-500 text-blue-700 font-bold' 
                                                            : 'bg-white border-slate-100 text-slate-600 hover:border-slate-200'}
                                                    `}
                                                >
                                                    <span className="capitalize text-xs font-semibold tracking-wide">{status}</span>
                                                    {newStatus === status && <CheckCircle2 className="h-4 w-4" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Billing Approach & Override Rates */}
                                <div className="space-y-4">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider">Billing Settings</h4>

                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black uppercase text-slate-500">Billing Approach</label>
                                        <select 
                                            value={billingApproach}
                                            onChange={(e) => setBillingApproach(e.target.value)}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Use Package Default</option>
                                            <option value="unit_fixed">Fixed Rate per Unit</option>
                                            <option value="unit_commission">Commission-Based Approach</option>
                                        </select>
                                    </div>

                                    {/* Override rates form */}
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Rate Overrides (Optional)</p>
                                        
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500">Custom Price per Unit (KES)</label>
                                            <input 
                                                type="number"
                                                step="0.01"
                                                value={priceOverride}
                                                onChange={(e) => setPriceOverride(e.target.value)}
                                                placeholder="e.g. 150.00"
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                                            />
                                            <p className="text-[9px] text-slate-400 font-medium">Leave empty to inherit package rate.</p>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500">Custom Commission Percentage (%)</label>
                                            <input 
                                                type="number"
                                                step="0.01"
                                                value={commissionOverride}
                                                onChange={(e) => setCommissionOverride(e.target.value)}
                                                placeholder="e.g. 5.5"
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                                            />
                                            <p className="text-[9px] text-slate-400 font-medium">Leave empty to inherit package commission.</p>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black uppercase text-slate-500">Auto-Billing M-Pesa Number</label>
                                        <input 
                                            type="text"
                                            value={billingMpesaNumber}
                                            onChange={(e) => setBillingMpesaNumber(e.target.value)}
                                            placeholder="e.g. 2547XXXXXXXX"
                                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:border-blue-500 transition-colors font-mono"
                                        />
                                        <p className="text-[9px] text-slate-400 font-medium">Specify the M-Pesa phone number where subscription bills will be automatically paid to.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-slate-50 flex gap-3">
                                <button 
                                    onClick={() => setStatusModal(false)}
                                    className="flex-1 py-3 bg-slate-50 text-slate-500 text-sm font-bold rounded-2xl hover:bg-slate-100 transition-all"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleUpdateStatus}
                                    disabled={updating}
                                    className="flex-1 py-3 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all flex items-center justify-center gap-2"
                                >
                                    {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save Changes</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstitutionDetails;
